import { Host, h, readTask, writeTask } from "@stencil/core";
import { focusComposedPath, mouseEventModifierKey } from "../../common/helpers";
import { scrollToEdge } from "../../../common/scroll-to-edge";
const TREE_ITEM_TAG_NAME = "ch-tree-view-item";
const TREE_TAG_NAME = "ch-tree-view";
// Selectors
// const CHECKED_ITEMS = `${TREE_ITEM_TAG_NAME}[checked]`;
const ITEM_SELECTOR = (treeItemId) => `${TREE_ITEM_TAG_NAME}[id="${treeItemId}"]`;
const TEXT_FORMAT = "text/plain";
const ARROW_DOWN_KEY = "ArrowDown";
const ARROW_UP_KEY = "ArrowUp";
const EDIT_KEY = "F2";
const isTreeItem = (element) => element.tagName.toLowerCase() === TREE_ITEM_TAG_NAME;
const getFocusedTreeItem = () => focusComposedPath().find(isTreeItem);
const canMoveTreeItemFocus = (treeItem) => treeItem && !treeItem.editing;
const getDroppableZoneKey = (newContainerId, draggedItems) => `"newContainerId":"${newContainerId}","metadata":"${JSON.stringify(draggedItems)}"`;
const POSITION_X_DRAG_CUSTOM_VAR = "--ch-tree-view-dragging-item-x";
const POSITION_Y_DRAG_CUSTOM_VAR = "--ch-tree-view-dragging-item-y";
export class ChTreeView {
    constructor() {
        this.draggingInTheDocument = false;
        this.draggingInTree = false;
        this.multiSelection = false;
        this.openSubTreeCountdown = 750;
        this.selectedItemsCallback = undefined;
        this.scrollToEdgeOnDrag = true;
        this.waitDropProcessing = false;
    }
    // @todo TODO: Check if key codes works in Safari
    #keyDownEvents = {
        [ARROW_DOWN_KEY]: event => {
            const treeItem = getFocusedTreeItem();
            if (!canMoveTreeItemFocus(treeItem)) {
                return;
            }
            event.preventDefault();
            treeItem.focusNextItem(mouseEventModifierKey(event));
        },
        [ARROW_UP_KEY]: event => {
            const treeItem = getFocusedTreeItem();
            if (!canMoveTreeItemFocus(treeItem)) {
                return;
            }
            event.preventDefault();
            treeItem.focusPreviousItem(mouseEventModifierKey(event));
        },
        [EDIT_KEY]: event => {
            const treeItem = getFocusedTreeItem();
            if (!treeItem || !treeItem.editable) {
                return;
            }
            event.preventDefault();
            treeItem.editing = true;
        }
    };
    #draggingSelectedItems = false;
    #needForRAF = true; // To prevent redundant RAF (request animation frame) calls
    #lastDragEvent;
    #openSubTreeTimeout;
    /**
     * Cache to avoid duplicate requests when checking the droppable zone in the
     * same drag event.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #validDroppableZoneCache = new Map();
    #dragStartTimestamp; // Useful to avoid race conditions where the server response is slow
    #draggedItems;
    // Refs
    #currentDraggedItem;
    #lastOpenSubTreeItem;
    /**
     * Text displayed when dragging an item.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #dragInfo;
    #draggedIds = [];
    #draggedParentIds = [];
    el;
    /**
     * Fired when an element attempts to enter in a droppable zone where the tree
     * has no information about the validity of the drop.
     */
    droppableZoneEnter;
    /**
     * Fired when an item is expanded or collapsed.
     */
    expandedItemChange;
    /**
     * Fired when an element displays its contextmenu.
     */
    itemContextmenu;
    /**
     * Fired when the dragged items are dropped in another item of the tree.
     */
    itemsDropped;
    /**
     * Fired when the selected items change.
     */
    selectedItemsChange;
    handleContextMenuEvent(event) {
        const treeItem = event.target.closest(TREE_ITEM_TAG_NAME);
        if (!treeItem) {
            return;
        }
        event.preventDefault();
        this.itemContextmenu.emit({
            id: treeItem.id,
            itemRef: treeItem,
            metadata: treeItem.metadata,
            contextmenuEvent: event
        });
    }
    // Set edit mode in items
    handleKeyDownEvents(event) {
        const keyHandler = this.#keyDownEvents[event.key];
        if (keyHandler) {
            keyHandler(event);
        }
    }
    // We can't use capture, because the dataTransfer info would not be defined
    // Also, we cant use capture and setTimeout with 0 seconds, because the
    // getData method can only be accessed during the dragstart and drop event
    handleDragStart(event) {
        // Reset the validity of the droppable zones with each new drag start
        this.#validDroppableZoneCache.clear();
        // If there is no data, the dragstart does not achieve the interface required
        const data = event.dataTransfer.getData(TEXT_FORMAT);
        if (data === "") {
            return;
        }
        try {
            // Try to parse the data
            const paredData = JSON.parse(data);
            this.#draggedItems = paredData;
            this.draggingInTheDocument = true;
            this.#dragStartTimestamp = new Date().getTime();
        }
        catch {
            // Empty
        }
    }
    handleDragEnd() {
        this.draggingInTheDocument = false;
    }
    handleDragEnter(event) {
        this.#cancelSubTreeOpening(null, true);
        event.stopPropagation();
        const containerTarget = event.target;
        // Check if it is a valid item
        if (containerTarget.tagName.toLowerCase() !== TREE_ITEM_TAG_NAME) {
            return;
        }
        this.#lastOpenSubTreeItem = containerTarget;
        this.#openSubTreeAfterCountdown(containerTarget);
        if (this.#validDroppableZone(event) === "valid") {
            containerTarget.dragState = "enter";
        }
    }
    handleDragLeave(event) {
        const currentTarget = event.target;
        if (currentTarget.tagName.toLowerCase() !== TREE_ITEM_TAG_NAME) {
            return;
        }
        const treeItem = currentTarget;
        treeItem.dragState = "none";
        this.#cancelSubTreeOpening(treeItem);
    }
    #cancelSubTreeOpening = (treeItem, forceClear = false) => {
        if (this.#lastOpenSubTreeItem === treeItem || forceClear) {
            clearTimeout(this.#openSubTreeTimeout);
            this.#lastOpenSubTreeItem = null;
        }
    };
    handleItemDrop(event) {
        event.stopPropagation();
        this.#cancelSubTreeOpening(null, true);
        const newContainer = event.target;
        const draggedItems = JSON.parse(event.dataTransfer.getData(TEXT_FORMAT));
        // The droppable zone must be checked, even if it was marked as not valid
        // @todo Try to drop an item with high delays in droppable zone checking
        if (this.#validDroppableZone(event) !== "valid") {
            return;
        }
        this.itemsDropped.emit({
            newContainer: { id: newContainer.id, metadata: newContainer.metadata },
            draggingSelectedItems: this.#draggingSelectedItems,
            draggedItems: draggedItems,
            dropInTheSameTree: this.draggingInTree
        });
    }
    handleItemDragStart(event) {
        // Avoid bubbling as this event can listened in other components (e.g. ch-flexible-layout)
        event.stopPropagation();
        document.body.addEventListener("dragover", this.#trackItemDrag, {
            capture: true
        });
        this.#currentDraggedItem = event.target;
        const allItemsCanBeDragged = this.#checkDragValidityAndUpdateDragInfo(event.detail);
        if (!allItemsCanBeDragged) {
            // This effect disables drop interactions in all page elements, so there
            // is no need to capture and prevent the drop event in the window
            event.detail.dragEvent.dataTransfer.effectAllowed = "none";
            return;
        }
        this.draggingInTree = true;
        if (this.scrollToEdgeOnDrag) {
            this.#fixScrollPositionOnDrag();
        }
    }
    handleItemDragEnd() {
        this.draggingInTree = false;
        document.body.removeEventListener("dragover", this.#trackItemDrag, {
            capture: true
        });
        // Reset not allowed droppable ids
        this.#resetVariables();
    }
    handleSelectedItemChange(event) {
        event.stopPropagation();
        const selectedItemInfo = event.detail;
        const selectedItemsInfo = this.selectedItemsCallback();
        // If the Control key was not pressed or multi selection is disabled,
        // remove all selected items
        if (!selectedItemInfo.ctrlKeyPressed || !this.multiSelection) {
            // Clear selected items
            selectedItemsInfo.clear();
        }
        // If the item is selected, add it to list
        if (selectedItemInfo.selected) {
            selectedItemsInfo.set(selectedItemInfo.id, selectedItemInfo);
        }
        else {
            selectedItemsInfo.delete(selectedItemInfo.id);
        }
        // Sync with UI model
        this.selectedItemsChange.emit(selectedItemsInfo);
    }
    /**
     * Given an item id, it scrolls into the item view.
     */
    async scrollIntoVisible(treeItemId) {
        readTask(() => {
            const itemRef = this.el.querySelector(ITEM_SELECTOR(treeItemId));
            if (!itemRef) {
                return;
            }
            writeTask(() => {
                itemRef.scrollIntoView();
            });
        });
    }
    /**
     * Update the information about the valid droppable zones.
     * @param requestTimestamp Time where the request to the server was made. Useful to avoid having old information.
     * @param newContainerId ID of the container where the drag is trying to be made.
     * @param draggedItems Information about the dragged items.
     * @param validDrop Current state of the droppable zone.
     */
    async updateValidDropZone(requestTimestamp, newContainerId, draggedItems, validDrop) {
        if (!this.draggingInTheDocument ||
            requestTimestamp <= this.#dragStartTimestamp) {
            return;
        }
        const droppableZoneKey = getDroppableZoneKey(newContainerId, draggedItems);
        this.#validDroppableZoneCache.set(droppableZoneKey, validDrop ? "valid" : "invalid");
        const shouldUpdateDragEnterInCurrentContainer = this.#lastOpenSubTreeItem?.id === newContainerId;
        if (shouldUpdateDragEnterInCurrentContainer) {
            this.#lastOpenSubTreeItem.dragState = "enter";
        }
    }
    #getTreeViewItemRef = itemId => this.el.querySelector(ITEM_SELECTOR(itemId));
    #validDroppableZone = (event) => {
        const containerTarget = event.target;
        const cacheKey = getDroppableZoneKey(containerTarget.id, this.#draggedItems);
        let droppableZoneState = this.#validDroppableZoneCache.get(cacheKey);
        // Invalidate the cache, because the item is no longer waiting for its content to be downloaded
        if (droppableZoneState === "temporal-invalid" &&
            !containerTarget.lazyLoad &&
            !containerTarget.downloading) {
            droppableZoneState = null;
        }
        if (droppableZoneState != null) {
            return droppableZoneState;
        }
        // Do not show drop zones if:
        //   - The effect does not allow it.
        //   - The drop is disabled in the container target.
        //   - When dragging in the same tree, don't mark droppable zones if they are
        //     the dragged items or their direct parents.
        if (event.dataTransfer.effectAllowed === "none" ||
            containerTarget.dropDisabled ||
            (this.draggingInTree &&
                (this.#draggedIds.includes(containerTarget.id) ||
                    this.#draggedParentIds.includes(containerTarget.id)))) {
            this.#validDroppableZoneCache.set(cacheKey, "invalid");
            return "invalid";
        }
        // Disable drops when items need to lazy load their content first
        if (containerTarget.lazyLoad || containerTarget.downloading) {
            this.#validDroppableZoneCache.set(cacheKey, "temporal-invalid");
            return "temporal-invalid";
        }
        this.#validDroppableZoneCache.set(cacheKey, "checking");
        this.droppableZoneEnter.emit({
            newContainer: {
                id: containerTarget.id,
                metadata: containerTarget.metadata
            },
            draggedItems: this.#draggedItems
        });
        return "checking";
    };
    #openSubTreeAfterCountdown = (currentTarget) => {
        if (currentTarget.leaf || currentTarget.expanded) {
            return;
        }
        this.#openSubTreeTimeout = setTimeout(() => {
            currentTarget.expanded = true;
            this.expandedItemChange.emit({ id: currentTarget.id, expanded: true });
        }, this.openSubTreeCountdown);
    };
    #trackItemDrag = (event) => {
        const draggingInATree = event.target.closest(TREE_TAG_NAME) !== null;
        // The Tree View must be the only element that processes the "dragover"
        // event. Any other handler that processes this event can modify the
        // `dropEffect` an thus break the drag and drop implementation
        if (draggingInATree) {
            event.stopImmediatePropagation();
        }
        event.preventDefault();
        this.#lastDragEvent = event;
        this.#updateDropEffect(event);
        if (!this.#needForRAF) {
            return;
        }
        this.#needForRAF = false; // No need to call RAF up until next frame
        requestAnimationFrame(() => {
            this.#needForRAF = true; // RAF now consumes the movement instruction so a new one can come
            this.el.style.setProperty(POSITION_X_DRAG_CUSTOM_VAR, `${this.#lastDragEvent.clientX}px`);
            this.el.style.setProperty(POSITION_Y_DRAG_CUSTOM_VAR, `${this.#lastDragEvent.clientY}px`);
        });
    };
    #updateDropEffect = (event) => {
        const itemTarget = event.target;
        // Check if it is a valid item
        if (itemTarget.tagName.toLowerCase() !== TREE_ITEM_TAG_NAME ||
            itemTarget.closest(TREE_TAG_NAME) !== this.el) {
            return;
        }
        const droppableZoneState = this.#validDroppableZone(event);
        if (droppableZoneState === "invalid" ||
            droppableZoneState === "temporal-invalid") {
            event.dataTransfer.dropEffect = "none";
        }
    };
    #resetVariables = () => {
        this.#draggedIds = [];
        this.#draggedParentIds = [];
    };
    /**
     * First, it check if all items can be dragged. If so, it updates the
     * dataTransfer in the drag event to store the ids and metadata of the
     * dragged items. Also it updates the visual information of the dragged
     * items.
     * @returns If all selected items can be dragged.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #checkDragValidityAndUpdateDragInfo = (dragInfo) => {
        const draggedElement = dragInfo.elem;
        const selectedItemsInfo = this.selectedItemsCallback();
        const isDraggingSelectedItems = selectedItemsInfo.has(draggedElement.id);
        this.#draggingSelectedItems = isDraggingSelectedItems;
        let dataTransferInfo = [];
        let dragIsEnabledForAllItems;
        if (isDraggingSelectedItems) {
            const selectedItemKeys = [...selectedItemsInfo.keys()];
            const selectedItemValues = [...selectedItemsInfo.values()];
            const selectedItemCount = selectedItemKeys.length;
            dragIsEnabledForAllItems = selectedItemValues.every(el => !this.#getTreeViewItemRef(el.id).dragDisabled);
            this.#draggedIds = selectedItemKeys;
            dataTransferInfo = selectedItemValues.map(el => ({
                id: el.id,
                metadata: el.metadata
            }));
            this.#dragInfo =
                selectedItemCount === 1
                    ? draggedElement.caption
                    : selectedItemCount.toString();
        }
        else {
            dragIsEnabledForAllItems = !draggedElement.dragDisabled;
            dataTransferInfo = [
                { id: draggedElement.id, metadata: draggedElement.metadata }
            ];
            this.#draggedIds = [draggedElement.id];
            this.#dragInfo = draggedElement.caption;
        }
        this.#getDirectParentsOfDraggableItems(isDraggingSelectedItems);
        // Update drag event info
        const data = JSON.stringify(dataTransferInfo);
        dragInfo.dragEvent.dataTransfer.setData(TEXT_FORMAT, data);
        // We must keep the data binding and processing even if there is an item
        // that can't be dragged, otherwise, other trees or element might behave
        // unexpected when a dragstart event comes
        return dragIsEnabledForAllItems;
    };
    #fixScrollPositionOnDrag = () => {
        if (!this.draggingInTree || !this.#lastDragEvent) {
            return;
        }
        requestAnimationFrame(() => {
            scrollToEdge(this.#lastDragEvent, this.el, 10, 30);
            requestAnimationFrame(this.#fixScrollPositionOnDrag);
        });
    };
    #getDirectParentsOfDraggableItems = (draggingSelectedItems) => {
        if (!draggingSelectedItems) {
            const parentTreeItemElem = this.#currentDraggedItem.parentElement;
            if (parentTreeItemElem.tagName.toLowerCase() === TREE_ITEM_TAG_NAME) {
                this.#draggedParentIds.push(parentTreeItemElem.id);
            }
            return;
        }
        // Dragging selected items
        const selectedItemsInfo = this.selectedItemsCallback();
        selectedItemsInfo.forEach(selectedItem => {
            const parentId = selectedItem.parentId;
            // parentId === "" when the item is in the first level of the tree
            if (parentId !== "") {
                this.#draggedParentIds.push(parentId);
            }
        });
    };
    disconnectedCallback() {
        this.#resetVariables();
        // Remove dragover body event
        this.handleItemDragEnd();
    }
    render() {
        return (h(Host, { class: {
                "ch-tree-view-dragging-item": this.draggingInTheDocument,
                "ch-tree-view-not-dragging-item": !this.draggingInTheDocument,
                "ch-tree-view--dragging-selected-items": this.draggingInTree && this.#draggingSelectedItems,
                "ch-tree-view-waiting-drop-processing": this.waitDropProcessing
            } }, h("div", { role: "tree", part: "tree-x-container", "aria-multiselectable": this.multiSelection.toString(), class: "ch-tree-view-container" }, h("slot", null)), this.draggingInTree && (h("span", { "aria-hidden": "true", class: "ch-tree-view-drag-info" }, this.#dragInfo))));
    }
    static get is() { return "ch-tree-view"; }
    static get originalStyleUrls() {
        return {
            "$": ["tree-view.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tree-view.css"]
        };
    }
    static get properties() {
        return {
            "multiSelection": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to allow multi selection of the items."
                },
                "attribute": "multi-selection",
                "reflect": false,
                "defaultValue": "false"
            },
            "openSubTreeCountdown": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you specify the time (in ms) that the mouse must be\nover in a subtree to open it when dragging."
                },
                "attribute": "open-sub-tree-countdown",
                "reflect": false,
                "defaultValue": "750"
            },
            "selectedItemsCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "() => Map<\n    string,\n    TreeViewItemSelectedInfo\n  >",
                    "resolved": "() => Map<string, TreeViewItemSelectedInfo>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemSelectedInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemSelectedInfo"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Callback that is executed to get the current selected items."
                }
            },
            "scrollToEdgeOnDrag": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to scroll in the tree when dragging an item near the edges of the\ntree."
                },
                "attribute": "scroll-to-edge-on-drag",
                "reflect": false,
                "defaultValue": "true"
            },
            "waitDropProcessing": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you specify if the tree is waiting to process the drop\nof items."
                },
                "attribute": "wait-drop-processing",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get states() {
        return {
            "draggingInTheDocument": {},
            "draggingInTree": {}
        };
    }
    static get events() {
        return [{
                "method": "droppableZoneEnter",
                "name": "droppableZoneEnter",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an element attempts to enter in a droppable zone where the tree\nhas no information about the validity of the drop."
                },
                "complexType": {
                    "original": "TreeViewDropCheckInfo",
                    "resolved": "{ newContainer: GxDataTransferInfo; draggedItems: GxDataTransferInfo[]; }",
                    "references": {
                        "TreeViewDropCheckInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDropCheckInfo"
                        }
                    }
                }
            }, {
                "method": "expandedItemChange",
                "name": "expandedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an item is expanded or collapsed."
                },
                "complexType": {
                    "original": "TreeViewItemExpandedInfo",
                    "resolved": "{ id: string; expanded: boolean; }",
                    "references": {
                        "TreeViewItemExpandedInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemExpandedInfo"
                        }
                    }
                }
            }, {
                "method": "itemContextmenu",
                "name": "itemContextmenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an element displays its contextmenu."
                },
                "complexType": {
                    "original": "TreeViewItemContextMenu",
                    "resolved": "{ id: string; itemRef: HTMLChTreeViewItemElement; metadata: string; contextmenuEvent: PointerEvent; }",
                    "references": {
                        "TreeViewItemContextMenu": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemContextMenu"
                        }
                    }
                }
            }, {
                "method": "itemsDropped",
                "name": "itemsDropped",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the dragged items are dropped in another item of the tree."
                },
                "complexType": {
                    "original": "TreeViewDataTransferInfo",
                    "resolved": "{ newContainer: GxDataTransferInfo; draggedItems: GxDataTransferInfo[]; draggingSelectedItems: boolean; dropInTheSameTree: boolean; }",
                    "references": {
                        "TreeViewDataTransferInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewDataTransferInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemsChange",
                "name": "selectedItemsChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected items change."
                },
                "complexType": {
                    "original": "Map<string, TreeViewItemSelectedInfo>",
                    "resolved": "Map<string, TreeViewItemSelectedInfo>",
                    "references": {
                        "Map": {
                            "location": "global",
                            "id": "global::Map"
                        },
                        "TreeViewItemSelectedInfo": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemSelectedInfo"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "scrollIntoVisible": {
                "complexType": {
                    "signature": "(treeItemId: string) => Promise<void>",
                    "parameters": [{
                            "name": "treeItemId",
                            "type": "string",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Given an item id, it scrolls into the item view.",
                    "tags": []
                }
            },
            "updateValidDropZone": {
                "complexType": {
                    "signature": "(requestTimestamp: number, newContainerId: string, draggedItems: GxDataTransferInfo[], validDrop: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "requestTimestamp",
                            "type": "number",
                            "docs": "Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "newContainerId",
                            "type": "string",
                            "docs": "ID of the container where the drag is trying to be made."
                        }, {
                            "name": "draggedItems",
                            "type": "GxDataTransferInfo[]",
                            "docs": "Information about the dragged items."
                        }, {
                            "name": "validDrop",
                            "type": "boolean",
                            "docs": "Current state of the droppable zone."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "GxDataTransferInfo": {
                            "location": "import",
                            "path": "../../../common/types",
                            "id": "src/common/types.ts::GxDataTransferInfo"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the information about the valid droppable zones.",
                    "tags": [{
                            "name": "param",
                            "text": "requestTimestamp Time where the request to the server was made. Useful to avoid having old information."
                        }, {
                            "name": "param",
                            "text": "newContainerId ID of the container where the drag is trying to be made."
                        }, {
                            "name": "param",
                            "text": "draggedItems Information about the dragged items."
                        }, {
                            "name": "param",
                            "text": "validDrop Current state of the droppable zone."
                        }]
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get listeners() {
        return [{
                "name": "contextmenu",
                "method": "handleContextMenuEvent",
                "target": undefined,
                "capture": true,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDownEvents",
                "target": undefined,
                "capture": true,
                "passive": false
            }, {
                "name": "dragstart",
                "method": "handleDragStart",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "dragend",
                "method": "handleDragEnd",
                "target": "window",
                "capture": true,
                "passive": true
            }, {
                "name": "dragenter",
                "method": "handleDragEnter",
                "target": undefined,
                "capture": true,
                "passive": true
            }, {
                "name": "dragleave",
                "method": "handleDragLeave",
                "target": undefined,
                "capture": true,
                "passive": true
            }, {
                "name": "drop",
                "method": "handleItemDrop",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "itemDragStart",
                "method": "handleItemDragStart",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "itemDragEnd",
                "method": "handleItemDragEnd",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "selectedItemChange",
                "method": "handleSelectedItemChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=tree-view.js.map
